import { computed, inject, Injectable, signal } from '@angular/core';
import { ApplicationVersion } from '@core/interfaces/application-version.interface';
import {
  APPLICATION_CURRENT_VERSION,
  APPLICATION_VERSION_BROWSER,
  APPLICATION_VERSION_CONFIG,
} from '@core/tokens/application-version.token';

/**
 * Service for checking if our application is up to date with the newest deploy.
 */
@Injectable({ providedIn: 'root' })
export class ApplicationVersionService {
  private readonly browser = inject(APPLICATION_VERSION_BROWSER);
  private readonly config = inject(APPLICATION_VERSION_CONFIG);

  private readonly currentVersion = inject(APPLICATION_CURRENT_VERSION);

  private readonly latestVersion = signal<ApplicationVersion | null>(null);
  private readonly dismissedVersionKey = signal<string | null>(null);

  private started = false;
  private intervalId: ReturnType<Window['setInterval']> | null = null;

  readonly updateAvailable = computed(() => {
    const latestVersion = this.latestVersion();

    if (!latestVersion) return false;

    return this.isUpdateAvailable(latestVersion);
  });

  start(): void {
    if (this.started) return;
    this.started = true;

    void this.checkForUpdate();

    this.intervalId = this.browser.setInterval(() => void this.checkForUpdate(), this.config.pollInterval);
    this.browser.addEventListener('focus', this.checkForUpdateOnEvent);
    this.browser.addEventListener('online', this.checkForUpdateOnEvent);
    this.browser.document.addEventListener('visibilitychange', this.checkForUpdateWhenVisible);
  }

  async checkForUpdate(): Promise<void> {
    const version = await this.fetchLatestVersion();

    if (!version) {
      console.info('[ApplicationVersion] Version check skipped: no valid version response.');
      return;
    }

    this.latestVersion.set(version);
  }

  dismissUpdate(): void {
    const latestVersion = this.latestVersion();
    if (!latestVersion) return;

    this.dismissedVersionKey.set(this.getVersionKey(latestVersion));
  }

  stop(): void {
    if (!this.started) return;

    if (this.intervalId !== null) {
      this.browser.clearInterval(this.intervalId);
    }

    this.browser.removeEventListener('focus', this.checkForUpdateOnEvent);
    this.browser.removeEventListener('online', this.checkForUpdateOnEvent);
    this.browser.document.removeEventListener('visibilitychange', this.checkForUpdateWhenVisible);

    this.intervalId = null;
    this.started = false;
  }

  private readonly checkForUpdateOnEvent = (): void => void this.checkForUpdate();

  private readonly checkForUpdateWhenVisible = (): void => {
    if (this.browser.document.visibilityState !== 'visible') return;
    void this.checkForUpdate();
  };

  private async fetchLatestVersion(): Promise<ApplicationVersion | null> {
    try {
      const response = await this.browser.fetch(this.getVersionUrl(), {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      });

      if (!response.ok) return null;

      const version = await response.json();
      return isApplicationVersion(version) ? version : null;
    } catch {
      return null;
    }
  }

  private getVersionUrl(): string {
    const versionUrl = new URL(this.config.versionUrl, this.browser.document.baseURI);
    versionUrl.searchParams.set('t', Date.now().toString());

    return versionUrl.toString();
  }

  private isUpdateAvailable(latestVersion: ApplicationVersion): boolean {
    return this.hasVersionChanged(latestVersion) && this.getVersionKey(latestVersion) !== this.dismissedVersionKey();
  }

  private hasVersionChanged(latestVersion: ApplicationVersion): boolean {
    return (
      latestVersion.version !== this.currentVersion.version || latestVersion.buildId !== this.currentVersion.buildId
    );
  }

  private getVersionKey(version: ApplicationVersion): string {
    return [version.version, version.buildId].join('|');
  }
}

function isApplicationVersion(value: unknown): value is ApplicationVersion {
  if (!isRecord(value)) return false;
  return typeof value['version'] === 'string' && typeof value['buildId'] === 'string';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}
